const dummyPdfBase64 =
  "JVBERi0xLjQKJSBEdW1teSBQREYKMSAwIG9iago8PCAvVHlwZSAvQ2F0YWxvZyAvUGFnZXMgMiAw\nIFIgPj4KZW5kb2JqCjIgMCBvYmoKPDwgL1R5cGUgL1BhZ2VzIC9LaWRzIFtdIC9Db3VudCAwID4+\nCmVuZG9iagp0cmFpbGVyCjw8IC9Sb290IDEgMCBSID4+CiUlRU9GCg==";
const dummyJpegBase64 =
  "/9j/4AAQSkZJRgABAQEASABIAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0a\nHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/2wBDAQkJCQwLDBgNDRgyIRwhMjIyMjIy\nMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjL/wAARCAABAAEDASIA\nAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQA\nAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3\nODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWm\np6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEA\nAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSEx\nBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElK\nU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3\nuLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwD5KKKK\n/P/Z";
const dummyPngBase64 =
  "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";

import type { SyntheticEmailScenario } from "@/lib/api/resources/agents";

const getDummyContent = (mimeType: string) => {
  if (mimeType.includes("pdf")) return dummyPdfBase64;
  if (mimeType.includes("png")) return dummyPngBase64;
  return dummyJpegBase64; // Default to JPEG
};

export const buildEml = (scenario: SyntheticEmailScenario): string => {
  const boundary = "U3jUUAuU"; // A consistent, safe boundary

  // Fold the date nicely (RFC 2822). `scenario.date` should be ISO or similar.
  let dateStr = new Date().toUTCString();
  if (scenario.date) {
    const parsedDate = new Date(scenario.date);
    if (!isNaN(parsedDate.getTime())) {
      dateStr = parsedDate.toUTCString();
    }
  }

  let eml = `From: ${scenario.senderName} <${scenario.senderEmail}>\r\n`;
  eml += `To: claims@example.com\r\n`;
  eml += `Subject: ${scenario.subject}\r\n`;
  eml += `Date: ${dateStr}\r\n`;
  eml += `MIME-Version: 1.0\r\n`;
  eml += `Content-Type: multipart/mixed; boundary=${boundary}\r\n\r\n`;

  eml += `--${boundary}\r\n`;
  eml += `Content-Type: text/plain; charset=utf-8\r\n\r\n`;
  eml += `${scenario.bodyText}\r\n`;

  if (scenario.attachments && Array.isArray(scenario.attachments)) {
    scenario.attachments.forEach((att) => {
      const mime = att.mimeType || "application/octet-stream";
      const base64Data = getDummyContent(mime);

      eml += `--${boundary}\r\n`;
      eml += `Content-Type: ${mime}; name="${att.filename}"\r\n`;
      eml += `Content-Transfer-Encoding: base64\r\n`;
      eml += `Content-Disposition: attachment; name="${att.filename}"; filename="${att.filename}"\r\n\r\n`;
      // Soft wrap the base64 string to 76 chars (not strictly required by all parsers but good practice)
      const wrappedData =
        base64Data.match(/.{1,76}/g)?.join("\r\n") || base64Data;
      eml += `${wrappedData}\r\n`;
    });
  }

  eml += `--${boundary}--\r\n`;
  return eml;
};

/**
 * Creates a File object from EML content with a dynamic webkitRelativePath.
 * We need this so the API client (which expects file.webkitRelativePath) uploads it correctly.
 */
export const createEmlFile = (
  emlContent: string,
  folderName: string,
  filename: string,
): File => {
  const file = new File([emlContent], filename, {
    type: "message/rfc822",
  });

  // Use Object.defineProperty to bypass the read-only getter on native browser File objects
  Object.defineProperty(file, "webkitRelativePath", {
    value: `${folderName}/${filename}`,
    writable: false,
  });

  return file;
};
